import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Plus, Trash2, Edit2, Send, Mail, Sparkles, X, Calendar, Users, Inbox, Trophy, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const KIND_LABELS = {
  RFQ: { name: 'Request For Quote', desc: 'Pricing request for known items' },
  RFP: { name: 'Request For Proposal', desc: 'Solution proposal for a need' },
  RFI: { name: 'Request For Information', desc: 'Capabilities & general inquiry' },
};

const RfxPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectVendor = searchParams.get('vendor');

  const [rfx, setRfx] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id, ...} = edit
  const [form, setForm] = useState(null);
  // Procurement loop state
  const [comparing, setComparing] = useState(null); // rfx object being compared
  const [comparisonData, setComparisonData] = useState(null);
  const [respDialog, setRespDialog] = useState(null); // null | { rfx, response: null|existing }
  const [respForm, setRespForm] = useState(null);
  const [awarding, setAwarding] = useState(null); // response_id being awarded
  const [createExpectedOrders, setCreateExpectedOrders] = useState(true);

  // iter48: Compose & send RFx via Resend
  const [composing, setComposing] = useState(null); // rfx being composed
  const [composeSubject, setComposeSubject] = useState('');
  const [composeHtml, setComposeHtml] = useState('');
  const [composeExtraTo, setComposeExtraTo] = useState('');
  const [sending, setSending] = useState(false);

  const openCompose = (r) => {
    const lines = (r.line_items || []).map((l, i) =>
      `<li>${i + 1}. ${l.item_sku || ''} ${l.item_name || ''} — qty ${l.quantity} ${l.uom || ''}` +
      (l.target_unit_cost ? ` (target $${l.target_unit_cost}/ea)` : '') +
      (l.notes ? ` — ${l.notes}` : '') + '</li>'
    ).join('');
    const html =
      `<p>Hello,</p>` +
      `<p>Please review the following <strong>${r.kind}</strong> request: <strong>${r.title}</strong>.</p>` +
      (r.deadline_date ? `<p><em>Response deadline:</em> ${r.deadline_date}</p>` : '') +
      (r.description ? `<p>${r.description}</p>` : '') +
      (lines ? `<p><strong>Line Items:</strong></p><ul>${lines}</ul>` : '') +
      `<p>Thank you,</p>`;
    setComposing(r);
    setComposeSubject(`${r.kind}: ${r.title}`);
    setComposeHtml(html);
    setComposeExtraTo('');
  };

  const sendCompose = async () => {
    if (!composing) return;
    setSending(true);
    try {
      const extra = composeExtraTo.split(/[,;\s]+/).filter(e => e && e.includes('@'));
      const res = await api().post(`/rfx/${composing.id}/send`, {
        subject: composeSubject,
        html: composeHtml,
        extra_to: extra,
      });
      const status = res.data?.status;
      if (status === 'sent') toast.success(`Sent to ${(res.data.recipients || []).length} recipient(s)`);
      else if (status === 'skipped') toast.error(`Skipped: ${res.data?.result?.reason || 'unknown'}`);
      else toast.error(`Resend error: ${res.data?.result?.error || 'unknown'}`);
      setComposing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rfxR, vR, iR] = await Promise.all([
        api().get('/rfx'),
        api().get('/vendors'),
        api().get('/items'),
      ]);
      setRfx(rfxR.data || []);
      setVendors((vR.data || []).filter(v => !v.archived));
      setItems(iR.data || []);
    } catch (e) {
      toast.error('Failed to load RFX');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-open new RFX form if linked from vendor profile
  useEffect(() => {
    if (preselectVendor && vendors.length > 0 && !editing) {
      openNew('RFQ', [preselectVendor]);
    }
  }, [preselectVendor, vendors.length]);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return rfx;
    return rfx.filter(r => r.kind === activeTab);
  }, [rfx, activeTab]);

  const openNew = (kind = 'RFQ', vendor_ids = []) => {
    setEditing({});
    setForm({
      kind,
      title: '',
      vendor_ids,
      deadline_date: '',
      description: '',
      line_items: [],
      seed_from_low_stock: false,
    });
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      kind: r.kind,
      title: r.title,
      vendor_ids: r.vendor_ids || [],
      deadline_date: r.deadline_date || '',
      description: r.description || '',
      line_items: r.line_items || [],
      seed_from_low_stock: false,
    });
  };

  const closeEdit = () => { setEditing(null); setForm(null); };

  const addLine = () => {
    setForm(f => ({
      ...f,
      line_items: [...f.line_items, { item_id: null, item_sku: '', item_name: '', quantity: 1, uom: 'EA', target_unit_cost: null, notes: '' }],
    }));
  };

  const updateLine = (idx, patch) => {
    setForm(f => ({
      ...f,
      line_items: f.line_items.map((l, i) => i === idx ? { ...l, ...patch } : l),
    }));
  };

  const setLineItemFromCatalog = (idx, item_id) => {
    if (!item_id) {
      updateLine(idx, { item_id: null, item_sku: '', item_name: '' });
      return;
    }
    const it = items.find(i => i.id === item_id);
    if (!it) return;
    updateLine(idx, {
      item_id: it.id,
      item_sku: it.item_sku,
      item_name: it.item_name,
      uom: it.base_uom || 'EA',
    });
  };

  const removeLine = (idx) => {
    setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  };

  const toggleVendor = (vid) => {
    setForm(f => ({
      ...f,
      vendor_ids: f.vendor_ids.includes(vid) ? f.vendor_ids.filter(x => x !== vid) : [...f.vendor_ids, vid],
    }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.vendor_ids.length === 0) { toast.error('Select at least one vendor'); return; }
    const payload = {
      ...form,
      line_items: form.line_items.map(l => ({
        ...l,
        quantity: parseFloat(l.quantity) || 0,
        target_unit_cost: l.target_unit_cost ? parseFloat(l.target_unit_cost) : null,
      })),
    };
    try {
      if (editing && editing.id) {
        await api().put(`/rfx/${editing.id}`, payload);
        toast.success('RFX updated');
      } else {
        await api().post('/rfx', payload);
        toast.success(`${form.kind} created`);
      }
      closeEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this RFX?')) return;
    try {
      await api().delete(`/rfx/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const generateMailto = (r) => {
    const vendorEmails = r.vendor_ids
      .map(vid => vendors.find(v => v.id === vid))
      .filter(v => v && v.contact_email)
      .map(v => v.contact_email);
    if (vendorEmails.length === 0) {
      toast.error('No vendors have contact_email set. Add an email to vendor profiles first.');
      return;
    }
    const lines = (r.line_items || []).map((l, i) => `${i + 1}. ${l.item_sku || ''} ${l.item_name || ''} — qty ${l.quantity} ${l.uom || ''}${l.target_unit_cost ? ` (target $${l.target_unit_cost}/ea)` : ''}${l.notes ? ` — ${l.notes}` : ''}`).join('\n');
    const body = [
      `Hello,`,
      ``,
      `Please review the following ${r.kind} request: ${r.title}`,
      r.deadline_date ? `Response deadline: ${r.deadline_date}` : '',
      r.description ? `\n${r.description}\n` : '',
      lines ? `Line Items:\n${lines}` : '',
      ``,
      `Thank you,`,
    ].filter(Boolean).join('\n');
    const subject = `${r.kind}: ${r.title}`;
    const mailto = `mailto:${vendorEmails.join(',')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  // ========== Procurement Loop handlers ==========

  const openComparison = async (r) => {
    setComparing(r);
    setComparisonData(null);
    try {
      const res = await api().get(`/rfx/${r.id}/comparison`);
      setComparisonData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load comparison');
    }
  };

  const closeComparison = () => { setComparing(null); setComparisonData(null); };

  const openNewResponse = (r, vendor_id) => {
    const vendor = vendors.find(v => v.id === vendor_id);
    setRespDialog({ rfx: r, response: null });
    setRespForm({
      vendor_id,
      vendor_name: vendor?.vendor_name || '',
      total_cost: null,
      notes: '',
      line_responses: (r.line_items || []).map(li => ({
        item_id: li.item_id,
        item_sku: li.item_sku,
        item_name: li.item_name,
        quantity: li.quantity,
        unit_cost: null,
        lead_time_days: null,
        moq: null,
        notes: '',
      })),
    });
  };

  const openEditResponse = (r, response) => {
    setRespDialog({ rfx: r, response });
    setRespForm({
      vendor_id: response.vendor_id,
      vendor_name: response.vendor_name,
      total_cost: response.total_cost,
      notes: response.notes || '',
      line_responses: (r.line_items || []).map(li => {
        const existing = (response.line_responses || []).find(lr => (lr.item_sku && lr.item_sku === li.item_sku) || (lr.item_id && lr.item_id === li.item_id));
        return {
          item_id: li.item_id,
          item_sku: li.item_sku,
          item_name: li.item_name,
          quantity: li.quantity,
          unit_cost: existing?.unit_cost ?? null,
          lead_time_days: existing?.lead_time_days ?? null,
          moq: existing?.moq ?? null,
          notes: existing?.notes ?? '',
        };
      }),
    });
  };

  const closeRespDialog = () => { setRespDialog(null); setRespForm(null); };

  const updateRespLine = (idx, patch) => {
    setRespForm(f => ({
      ...f,
      line_responses: f.line_responses.map((l, i) => i === idx ? { ...l, ...patch } : l),
    }));
  };

  const computedRespTotal = useMemo(() => {
    if (!respForm) return 0;
    return respForm.line_responses.reduce((acc, l) => acc + (parseFloat(l.unit_cost) || 0) * (parseFloat(l.quantity) || 0), 0);
  }, [respForm]);

  const saveResponse = async () => {
    if (!respForm.vendor_id) { toast.error('Vendor required'); return; }
    const payload = {
      vendor_id: respForm.vendor_id,
      total_cost: respForm.total_cost ? parseFloat(respForm.total_cost) : computedRespTotal,
      notes: respForm.notes,
      line_responses: respForm.line_responses.map(l => ({
        item_id: l.item_id, item_sku: l.item_sku,
        unit_cost: l.unit_cost === '' || l.unit_cost == null ? null : parseFloat(l.unit_cost),
        lead_time_days: l.lead_time_days === '' || l.lead_time_days == null ? null : parseInt(l.lead_time_days),
        moq: l.moq === '' || l.moq == null ? null : parseFloat(l.moq),
        notes: l.notes,
      })),
    };
    try {
      if (respDialog.response) {
        await api().put(`/rfx/${respDialog.rfx.id}/responses/${respDialog.response.id}`, payload);
        toast.success('Response updated');
      } else {
        await api().post(`/rfx/${respDialog.rfx.id}/responses`, payload);
        toast.success('Response captured');
      }
      closeRespDialog();
      // reload comparison if open
      if (comparing?.id === respDialog.rfx.id) await openComparison(comparing);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteResponse = async (resp) => {
    if (!window.confirm('Delete this response?')) return;
    try {
      await api().delete(`/rfx/${comparing.id}/responses/${resp.id}`);
      toast.success('Deleted');
      await openComparison(comparing);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const awardResponse = async (response_id) => {
    if (!window.confirm(`Award this RFX? This will append to price history${createExpectedOrders ? ' and create Expected Orders' : ''}.`)) return;
    setAwarding(response_id);
    try {
      const r = await api().post(`/rfx/${comparing.id}/award`, { response_id, create_expected_orders: createExpectedOrders });
      toast.success(`Awarded to ${r.data.awarded_vendor_name} · ${r.data.appended_price_history} price points · ${r.data.created_expected_orders} expected orders`);
      await openComparison(comparing);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Award failed'); }
    finally { setAwarding(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-slideIn" data-testid="rfx-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">RFQ / RFP / RFI</h1>
          <p className="text-slate-500 text-sm">Build vendor-facing requests with seeded product data.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openNew('RFI')} className="rounded-sm" data-testid="rfx-new-rfi-btn">
            <Plus className="w-4 h-4 mr-1" /> New RFI
          </Button>
          <Button variant="outline" onClick={() => openNew('RFP')} className="rounded-sm" data-testid="rfx-new-rfp-btn">
            <Plus className="w-4 h-4 mr-1" /> New RFP
          </Button>
          <Button onClick={() => openNew('RFQ')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="rfx-new-rfq-btn">
            <Plus className="w-4 h-4 mr-1" /> New RFQ
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="rfx-tab-all">All ({rfx.length})</TabsTrigger>
          <TabsTrigger value="RFQ" data-testid="rfx-tab-rfq">RFQ ({rfx.filter(r => r.kind === 'RFQ').length})</TabsTrigger>
          <TabsTrigger value="RFP" data-testid="rfx-tab-rfp">RFP ({rfx.filter(r => r.kind === 'RFP').length})</TabsTrigger>
          <TabsTrigger value="RFI" data-testid="rfx-tab-rfi">RFI ({rfx.filter(r => r.kind === 'RFI').length})</TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-0">
              {filtered.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No {activeTab === 'all' ? 'RFXs' : activeTab + 's'} yet</p>
                  <p className="text-sm">Click "New RFQ/RFP/RFI" to create one. Seed it from your low-stock items to save typing.</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kind</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Vendors</TableHead>
                      <TableHead className="text-right">Lines</TableHead>
                      <TableHead>Deadline</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(r => (
                      <TableRow key={r.id} data-testid={`rfx-row-${r.id}`}>
                        <TableCell>
                          <span className="text-xs px-2 py-0.5 mono font-semibold rounded-sm bg-slate-100 text-slate-700">{r.kind}</span>
                        </TableCell>
                        <TableCell className="font-medium">{r.title}</TableCell>
                        <TableCell className="text-xs text-slate-500">{(r.vendor_ids || []).length} vendor(s)</TableCell>
                        <TableCell className="text-right mono">{(r.line_items || []).length}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.deadline_date || '—'}</TableCell>
                        <TableCell><span className="status-badge status-draft">{r.status || 'draft'}</span></TableCell>
                        <TableCell className="text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openCompose(r)} title="Compose & send via Resend (rich text)" data-testid={`rfx-compose-${r.id}`}>
                              <Send className="w-4 h-4 text-emerald-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => generateMailto(r)} title="Open in mail client (mailto)" data-testid={`rfx-email-${r.id}`}>
                              <Mail className="w-4 h-4 text-orange-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openComparison(r)} title="Compare bids" data-testid={`rfx-compare-${r.id}`}>
                              <BarChart3 className="w-4 h-4 text-purple-600" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`rfx-edit-${r.id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`rfx-delete-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Comparison Dialog */}
      {comparing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeComparison}>
          <div className="bg-white rounded-sm max-w-6xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rfx-comparison-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" /> Bid Comparison — {comparing.title}
                </h2>
                <p className="text-xs text-slate-500">{comparing.kind} · deadline {comparing.deadline_date || '—'} · {(comparing.line_items || []).length} line(s)</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeComparison}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-5">
              {!comparisonData ? (
                <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
              ) : (
                <>
                  {/* Vendor totals + award */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold flex items-center gap-1"><Trophy className="w-4 h-4 text-amber-500" /> Bids ({comparisonData.totals.length})</h3>
                      <div className="flex items-center gap-2">
                        <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={createExpectedOrders} onChange={(e) => setCreateExpectedOrders(e.target.checked)} className="accent-emerald-500" data-testid="rfx-award-create-expected" /> Create Expected Orders on award</label>
                      </div>
                    </div>
                    {comparisonData.totals.length === 0 ? (
                      <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500" data-testid="rfx-no-responses">
                        <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No vendor responses yet. Add a response below.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-slate-200 rounded-sm">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Vendor</th>
                              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Total $</th>
                              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Lines Covered</th>
                              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Status</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparisonData.totals.map((t, idx) => {
                              const resp = comparisonData.responses.find(r => r.id === t.response_id);
                              const isWinner = comparing.awarded_response_id === t.response_id;
                              return (
                                <tr key={t.response_id} className={`border-t border-slate-100 ${isWinner ? 'bg-amber-50' : ''}`} data-testid={`rfx-bid-row-${t.response_id}`}>
                                  <td className="px-3 py-2 font-medium">
                                    {idx === 0 && <Trophy className="w-3 h-3 text-amber-500 inline mr-1" />}
                                    {t.vendor_name}
                                  </td>
                                  <td className="px-3 py-2 text-right mono font-semibold">${t.total_cost.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-right mono">{t.lines_covered} / {t.lines_total}</td>
                                  <td className="px-3 py-2">
                                    {isWinner ? <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-200 text-amber-800 font-semibold">AWARDED</span> : <span className="text-xs text-slate-400">Open</span>}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex justify-end gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => openEditResponse(comparing, resp)} data-testid={`rfx-resp-edit-${t.response_id}`}><Edit2 className="w-3 h-3" /></Button>
                                      {!comparing.awarded_response_id && (
                                        <>
                                          <Button size="sm" onClick={() => awardResponse(t.response_id)} disabled={awarding === t.response_id} className="bg-amber-600 hover:bg-amber-700 h-7 text-xs px-2" data-testid={`rfx-award-${t.response_id}`}>
                                            <Trophy className="w-3 h-3 mr-1" /> {awarding === t.response_id ? 'Awarding…' : 'Award'}
                                          </Button>
                                          <Button size="sm" variant="ghost" onClick={() => deleteResponse(resp)} className="text-red-600" data-testid={`rfx-resp-delete-${t.response_id}`}><Trash2 className="w-3 h-3" /></Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Line-by-line grid */}
                  {comparisonData.grid.length > 0 && comparisonData.totals.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Line-by-line Comparison</h3>
                      <div className="overflow-x-auto border border-slate-200 rounded-sm">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Line</th>
                              {comparisonData.totals.map(t => (
                                <th key={t.response_id} className="text-right px-3 py-2 text-xs uppercase tracking-wider text-slate-500">{t.vendor_name}</th>
                              ))}
                              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-emerald-600">Best</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comparisonData.grid.map((row, ri) => (
                              <tr key={ri} className="border-t border-slate-100" data-testid={`rfx-line-grid-${ri}`}>
                                <td className="px-3 py-2"><span className="mono font-medium">{row.line.item_sku}</span> — {row.line.item_name} <span className="text-xs text-slate-500">(qty {row.line.quantity})</span></td>
                                {row.responses.map(resp => {
                                  const isBest = resp.unit_cost === row.best_unit_cost && resp.unit_cost != null;
                                  return (
                                    <td key={resp.response_id} className={`px-3 py-2 text-right mono text-sm ${isBest ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-700'}`}>
                                      {resp.unit_cost == null ? <span className="text-slate-300">—</span> : `$${resp.unit_cost.toFixed(2)}`}
                                      {resp.lead_time_days != null && <div className="text-[10px] text-slate-400">{resp.lead_time_days}d</div>}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-right mono text-emerald-700 font-semibold">{row.best_unit_cost != null ? `$${row.best_unit_cost.toFixed(2)}` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Add Response */}
                  {!comparing.awarded_response_id && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Add Vendor Response</h3>
                      <div className="flex gap-2 flex-wrap">
                        {(comparing.vendor_ids || []).map(vid => {
                          const vendor = vendors.find(v => v.id === vid);
                          if (!vendor) return null;
                          const hasResponse = comparisonData.responses.some(r => r.vendor_id === vid);
                          return (
                            <Button
                              key={vid}
                              size="sm"
                              variant={hasResponse ? 'outline' : 'default'}
                              onClick={() => openNewResponse(comparing, vid)}
                              className={hasResponse ? '' : 'bg-slate-900 hover:bg-slate-800'}
                              data-testid={`rfx-add-response-${vid}`}
                            >
                              <Plus className="w-3 h-3 mr-1" /> {vendor.vendor_name} {hasResponse && '(re-enter)'}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Response Capture Dialog */}
      {respDialog && respForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeRespDialog}>
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rfx-response-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold">{respDialog.response ? 'Edit' : 'Capture'} Response — {respForm.vendor_name}</h2>
                <p className="text-xs text-slate-500">{respDialog.rfx.title} ({respDialog.rfx.kind})</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeRespDialog}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-3">
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Item</th>
                      <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Qty</th>
                      <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Unit $</th>
                      <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Lead (d)</th>
                      <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">MOQ</th>
                      <th className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {respForm.line_responses.map((l, idx) => (
                      <tr key={idx} className="border-t border-slate-100" data-testid={`rfx-resp-line-${idx}`}>
                        <td className="px-2 py-1"><span className="mono">{l.item_sku}</span> · {l.item_name}</td>
                        <td className="px-2 py-1 text-right mono text-slate-500">{l.quantity}</td>
                        <td className="px-2 py-1 w-24"><Input type="number" step="0.01" value={l.unit_cost ?? ''} onChange={(e) => updateRespLine(idx, { unit_cost: e.target.value })} className="rounded-sm h-8 text-xs mono text-right" placeholder="—" data-testid={`rfx-resp-unit-${idx}`} /></td>
                        <td className="px-2 py-1 w-20"><Input type="number" value={l.lead_time_days ?? ''} onChange={(e) => updateRespLine(idx, { lead_time_days: e.target.value })} className="rounded-sm h-8 text-xs mono text-right" placeholder="—" /></td>
                        <td className="px-2 py-1 w-20"><Input type="number" value={l.moq ?? ''} onChange={(e) => updateRespLine(idx, { moq: e.target.value })} className="rounded-sm h-8 text-xs mono text-right" placeholder="—" /></td>
                        <td className="px-2 py-1"><Input value={l.notes || ''} onChange={(e) => updateRespLine(idx, { notes: e.target.value })} className="rounded-sm h-8 text-xs" /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td colSpan={2} className="px-2 py-2 text-right text-xs uppercase tracking-wider text-slate-500">Computed Total</td>
                      <td className="px-2 py-2 text-right mono font-semibold">${computedRespTotal.toFixed(2)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div>
                <Label>Override Total (optional)</Label>
                <Input type="number" step="0.01" value={respForm.total_cost ?? ''} onChange={(e) => setRespForm(f => ({ ...f, total_cost: e.target.value }))} placeholder={`Default: $${computedRespTotal.toFixed(2)}`} className="rounded-sm mono w-48" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={respForm.notes} onChange={(e) => setRespForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="rounded-sm" placeholder="Vendor's response notes (terms, exclusions, etc.)" />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={closeRespDialog}>Cancel</Button>
              <Button onClick={saveResponse} className="bg-slate-900 hover:bg-slate-800" data-testid="rfx-resp-save-btn">{respDialog.response ? 'Update' : 'Save Response'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      {editing && form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeEdit}>
          <div className="bg-white rounded-sm max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rfx-dialog">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold">{editing.id ? `Edit ${form.kind}` : `New ${form.kind}`}</h2>
                <p className="text-xs text-slate-500">{KIND_LABELS[form.kind]?.desc}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeEdit}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Kind</Label>
                  <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="rfx-form-kind">
                    <option value="RFQ">RFQ — Quote</option>
                    <option value="RFP">RFP — Proposal</option>
                    <option value="RFI">RFI — Info</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Q3 Cap Bank Quote" className="rounded-sm" data-testid="rfx-form-title" />
                </div>
              </div>
              <div>
                <Label>Deadline</Label>
                <Input type="date" value={form.deadline_date} onChange={(e) => setForm({ ...form, deadline_date: e.target.value })} className="rounded-sm mono w-48" data-testid="rfx-form-deadline" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Context for the vendor..." rows={3} className="rounded-sm" data-testid="rfx-form-description" />
              </div>

              {/* Vendors */}
              <div>
                <Label className="flex items-center gap-1"><Users className="w-4 h-4" /> Send to Vendors *</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-1" data-testid="rfx-vendor-select">
                  {vendors.length === 0 ? (
                    <p className="text-xs text-slate-400 italic px-2">No active vendors. Add some first.</p>
                  ) : vendors.map(v => (
                    <label key={v.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded-sm cursor-pointer text-sm" data-testid={`rfx-vendor-option-${v.id}`}>
                      <input type="checkbox" checked={form.vendor_ids.includes(v.id)} onChange={() => toggleVendor(v.id)} className="accent-orange-500" />
                      <span className="font-medium">{v.vendor_name}</span>
                      <span className="text-xs text-slate-400 ml-auto mono">{v.contact_email || 'no email'}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <div className="flex gap-2 items-center">
                    {!editing.id && (
                      <label className="text-xs flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={form.seed_from_low_stock} onChange={(e) => setForm({ ...form, seed_from_low_stock: e.target.checked })} className="accent-orange-500" data-testid="rfx-seed-low-stock" />
                        <Sparkles className="w-3 h-3 text-orange-500" /> Seed from low-stock
                      </label>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="rfx-add-line-btn"><Plus className="w-3 h-3 mr-1" /> Add Line</Button>
                  </div>
                </div>
                {form.line_items.length === 0 ? (
                  <div className="border border-dashed border-slate-300 rounded-sm p-4 text-center text-xs text-slate-400">
                    {form.seed_from_low_stock ? 'Lines will auto-seed from low-stock items on save.' : 'No lines yet. Add a product or seed from low-stock.'}
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Item</th>
                          <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Qty</th>
                          <th className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-500">UOM</th>
                          <th className="text-right px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Target $</th>
                          <th className="text-left px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Notes</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.line_items.map((l, idx) => (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`rfx-line-${idx}`}>
                            <td className="px-2 py-1 min-w-[180px]">
                              <select value={l.item_id || ''} onChange={(e) => setLineItemFromCatalog(idx, e.target.value || null)} className="w-full border border-slate-200 rounded-sm h-8 px-2 text-xs">
                                <option value="">— freeform —</option>
                                {items.map(it => <option key={it.id} value={it.id}>{it.item_sku} · {it.item_name}</option>)}
                              </select>
                              {!l.item_id && (
                                <Input placeholder="SKU / Name" value={l.item_name} onChange={(e) => updateLine(idx, { item_name: e.target.value, item_sku: e.target.value })} className="rounded-sm h-7 text-xs mt-1" />
                              )}
                            </td>
                            <td className="px-2 py-1 w-20"><Input type="number" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} className="rounded-sm h-8 text-xs mono text-right" /></td>
                            <td className="px-2 py-1 w-16"><Input value={l.uom || ''} onChange={(e) => updateLine(idx, { uom: e.target.value })} className="rounded-sm h-8 text-xs mono" /></td>
                            <td className="px-2 py-1 w-24"><Input type="number" step="0.01" value={l.target_unit_cost ?? ''} onChange={(e) => updateLine(idx, { target_unit_cost: e.target.value })} className="rounded-sm h-8 text-xs mono text-right" placeholder="—" /></td>
                            <td className="px-2 py-1"><Input value={l.notes || ''} onChange={(e) => updateLine(idx, { notes: e.target.value })} className="rounded-sm h-8 text-xs" /></td>
                            <td className="px-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={closeEdit}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="rfx-save-btn">{editing.id ? 'Save' : `Create ${form.kind}`}</Button>
            </div>
          </div>
        </div>
      )}

      {/* iter48: Compose & send via Resend */}
      <Dialog open={!!composing} onOpenChange={(o) => !o && setComposing(null)}>
        <DialogContent className="max-w-3xl" data-testid="rfx-compose-dialog">
          <DialogHeader>
            <DialogTitle>Compose & Send — {composing?.kind}: {composing?.title}</DialogTitle>
            <DialogDescription>
              Sends a rich-text email via Resend to all vendor contact emails on this RFx.
              Honors the per-purpose recipient list configured in Admin → Email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input
                data-testid="rfx-compose-subject"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Body</Label>
              <RichTextEditor
                testId="rfx-compose-body"
                value={composeHtml}
                onChange={setComposeHtml}
                variables={['rfx_kind','rfx_title','deadline','vendor_name','line_items','requested_by']}
                placeholder="Compose the RFx outreach email…"
                minHeight={260}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Additional recipients (comma-separated)</Label>
              <Input
                data-testid="rfx-compose-extra-to"
                value={composeExtraTo}
                onChange={(e) => setComposeExtraTo(e.target.value)}
                placeholder="another@vendor.com, third@partner.com"
                className="rounded-sm"
              />
              <p className="text-[11px] text-slate-500">
                Vendor contact emails on the RFx are added automatically. Use this for cc'ing colleagues or extra contacts.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposing(null)} disabled={sending} data-testid="rfx-compose-cancel">Cancel</Button>
            <Button
              onClick={sendCompose}
              disabled={sending || !composeSubject || !composeHtml}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="rfx-compose-send"
            >
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RfxPage;
